import type {
  CreateRecipeInput,
  Recipe,
  RecipeStatus,
  RecipeTag,
  RecipeTagKind,
} from '@repo/types';

// Mirrors auth/api.ts: same-origin fetches, relative URLs (Vite proxies in dev,
// CloudFront routes /api/* in production). Every call sends the session cookie:
// the recipes query is public but personalized (the admin also receives draft
// recipes), and the mutations are admin-only.

/** Selection set matching the Recipe shape in @repo/types. */
const RECIPE_FIELDS = `
  id slug title cuisines proteins meal difficulty
  timeMinutes calories servings spice imageURL status
  ingredients { id quantity unit name spiced }
  steps
`;

const RECIPES_QUERY = `{
  recipes { ${RECIPE_FIELDS} }
}`;

const CREATE_RECIPE_MUTATION = `mutation CreateRecipe($input: CreateRecipeInput!) {
  createRecipe(input: $input) { ${RECIPE_FIELDS} }
}`;

const SET_RECIPE_STATUS_MUTATION = `mutation SetRecipeStatus($id: ID!, $status: String!) {
  setRecipeStatus(id: $id, status: $status) { ${RECIPE_FIELDS} }
}`;

const RECIPE_TAGS_QUERY = `{
  recipeTags { id kind name }
}`;

const CREATE_RECIPE_TAG_MUTATION = `mutation CreateRecipeTag($kind: String!, $name: String!) {
  createRecipeTag(kind: $kind, name: $name) { id kind name }
}`;

/** The cookbook, ordered by title (drafts included when the caller is the admin). */
export async function getRecipes(): Promise<Recipe[]> {
  const res = await fetch('/api/graphql', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: RECIPES_QUERY }),
  });
  if (!res.ok) throw new Error(`/api/graphql responded ${res.status}`);
  const body: { data?: { recipes: Recipe[] }; errors?: { message: string }[] } = await res.json();
  if (!body.data) {
    throw new Error(body.errors?.[0]?.message ?? 'GraphQL response carried no data');
  }
  return body.data.recipes;
}

/** The cuisine/meat vocabularies (recipe_tags rows), alphabetical within each kind. */
export async function getRecipeTags(): Promise<RecipeTag[]> {
  const res = await fetch('/api/graphql', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: RECIPE_TAGS_QUERY }),
  });
  if (!res.ok) throw new Error(`/api/graphql responded ${res.status}`);
  const body: { data?: { recipeTags: RecipeTag[] }; errors?: { message: string }[] } =
    await res.json();
  if (!body.data) {
    throw new Error(body.errors?.[0]?.message ?? 'GraphQL response carried no data');
  }
  return body.data.recipeTags;
}

/** Add a recipe to the cookbook (site owner only); resolves to the stored recipe. */
export async function createRecipe(input: CreateRecipeInput): Promise<Recipe> {
  const body = await runMutation<{ createRecipe: Recipe }>({
    query: CREATE_RECIPE_MUTATION,
    variables: { input },
  });
  return body.createRecipe;
}

/** Flip a recipe between draft and complete (site owner only). */
export async function setRecipeStatus({
  id,
  status,
}: {
  id: string;
  status: RecipeStatus;
}): Promise<Recipe> {
  const body = await runMutation<{ setRecipeStatus: Recipe }>({
    query: SET_RECIPE_STATUS_MUTATION,
    variables: { id, status },
  });
  return body.setRecipeStatus;
}

/** Grow the cuisine/meat vocabulary (site owner only); resolves to the stored tag. */
export async function createRecipeTag({
  kind,
  name,
}: {
  kind: RecipeTagKind;
  name: string;
}): Promise<RecipeTag> {
  const body = await runMutation<{ createRecipeTag: RecipeTag }>({
    query: CREATE_RECIPE_TAG_MUTATION,
    variables: { kind, name },
  });
  return body.createRecipeTag;
}

async function runMutation<Data extends object>({
  query,
  variables,
}: {
  query: string;
  variables: Record<string, unknown>;
}): Promise<Data> {
  const res = await fetch('/api/graphql', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`/api/graphql responded ${res.status}`);
  const body: { data?: Data | null; errors?: { message: string }[] } = await res.json();
  // Failed mutations arrive as 200s with an errors array (admin gate, validation,
  // duplicate slug) — surface the server's message, it's written for the form.
  if (!body.data) {
    throw new Error(body.errors?.[0]?.message ?? 'GraphQL response carried no data');
  }
  return body.data;
}
